import { useToast } from "@/components";
import { useDownloadMembershipChargeReceipt } from "@/fm/hooks/use_download_membership_charge_receipt";
import { useMediaQuery } from "@/hooks";
import { formatCurrency } from "@/lib/currency/currency";
import { useLocationQuery } from "@/offering/api";
import { useAuth } from "@/offering/auth_portal_provider";
import { Spacer } from "@/offering/spacer";
import { colors } from "@/offering_v2/components/colors";
import React, { useState } from "react";
import { useParams } from "react-router-dom";
import classNames from "classnames";
import {
  MembershipCharge,
  useMembershipChargesQuery,
} from "../hooks/use_membership_charges_query";
import { PortalPathParams } from "../hooks/use_portal_history";
import { useSubscriptionDetailsQuery } from "../hooks/use_subscription_details_query";
import { useSubscriptionOrderQuery } from "../hooks/use_subscription_order_query";
import { PortalBillBreakdownModal } from "./portal_bill_breakdown_modal";
import { PortalButton } from "./portal_button";
import styles from "./portal_membership_charges_table.module.css";
import { isNumber } from "lodash";
import { useMembershipManagementAnalytics } from "../hooks/use_membership_management_analytics";

const MEMBER_RECEIPT_COUNTRY = "US";

interface PortalMembershipChargesTableProps {
  partnerId: string;
  partnerName: string;
}

export function PortalMembershipChargesTable({
  partnerId,
  partnerName,
}: PortalMembershipChargesTableProps) {
  const { device } = useMediaQuery();
  const { subscriptionId } = useParams<PortalPathParams>();
  const { user } = useAuth();
  const toast = useToast();

  const analyticEvent = useMembershipManagementAnalytics();
  const { data: chargesData } = useMembershipChargesQuery(
    partnerId,
    subscriptionId
  );
  const { data: subscriptionDetails } = useSubscriptionDetailsQuery(
    partnerId,
    subscriptionId
  );
  const { data: order } = useSubscriptionOrderQuery(partnerId, subscriptionId);
  const { data: location } = useLocationQuery(subscriptionDetails?.locationId);

  // The same hook the dashboard's membership page uses, so both surfaces
  // produce the same PDF from the same generator.
  const {
    download: downloadReceipt,
    downloadingIds: receiptDownloadingIds,
    enabled: receiptEnabled,
  } = useDownloadMembershipChargeReceipt({
    order,
    partnerId,
    userId: user?.sub ?? "",
    partnerName,
    locationId: subscriptionDetails?.locationId,
    // "street, city": what the dashboard prints on a membership receipt
    // (Location#short_address), so the two PDFs match.
    locationAddress: location
      ? `${location.streetAddress}, ${location.city}`
      : undefined,
    membershipName: subscriptionDetails?.membership.name,
  });
  // Members download their own receipt only at US locations; members
  // elsewhere still get it by email. This is narrower than the dashboard,
  // where partner staff keep the download so they can produce one on request.
  const isReceiptCountry = location?.country === MEMBER_RECEIPT_COUNTRY;

  const [viewAllCharges, setViewAllCharges] = useState(false);
  const [viewingChargeDetails, setViewingChargeDetails] =
    useState<MembershipCharge>();

  const isMobile = device === "mobile";

  const columnClasses = isMobile
    ? {
        date: "col-5",
        membershipPlan: "d-none",
        paymentMethod: "d-none",
        total: "col-4",
        details: "col-3 text-end",
      }
    : {
        date: "col-2",
        membershipPlan: "col-4",
        paymentMethod: "col-3",
        total: "col-1",
        details: "col-2 text-end",
      };

  const handleClickChargeDetails = (charge: MembershipCharge) => {
    setViewingChargeDetails(charge);
    analyticEvent("Charge Details Click");
  };

  const handleDownloadReceipt = async (charge: MembershipCharge) => {
    analyticEvent("Download Receipt Click");
    try {
      await downloadReceipt(charge);
    } catch (e) {
      console.error("Error downloading receipt", e);
      toast.error({ message: "Couldn't download receipt. Please try again." });
    }
  };

  const mapChargeRow = (charge: MembershipCharge) => {
    const showReceipt =
      receiptEnabled && isReceiptCountry && charge.transactionType === "charge";

    return (
      <div key={charge.id} className="row  py-3 border-bottom">
        <div className={columnClasses.date}>{charge.paymentDate}</div>
        <div
          className={classNames(
            columnClasses.membershipPlan,
            styles.membershipPlan
          )}
        >
          {charge.membershipPlan.name}: {charge.membershipPlan.location}
        </div>
        <div className={columnClasses.paymentMethod}>
          {charge.promotionDescription && charge.totalPrice === 0 ? (
            <div style={{ color: colors.spinachCore }}>
              {charge.promotionDescription}
            </div>
          ) : (
            <div>
              <img
                className="me-3"
                src={charge.cardImageUrl}
                alt={charge.cardBrand}
                width={33}
                height={22}
              />
              Ending with {charge.cardEnding}
            </div>
          )}
        </div>
        {/* On mobile the row is too narrow for two text actions, so as on the
            details card above this table, the amount itself opens the details
            and the actions column keeps a single button. */}
        <div
          className={classNames(
            columnClasses.total,
            isMobile && "text-decoration-underline"
          )}
          onClick={
            isMobile ? () => handleClickChargeDetails(charge) : undefined
          }
        >
          {charge.transactionType === "charge" ? (
            formatCurrency(charge.totalPrice, charge.currency)
          ) : charge.transactionType === "refund" &&
            isNumber(charge.refundedAmount) ? (
            <div className="text-nowrap">
              {formatCurrency(
                -Math.abs(charge.refundedAmount),
                charge.currency
              )}
              <br />
              (Refunded)
            </div>
          ) : null}
        </div>
        <div
          className={classNames(
            columnClasses.details,
            "d-flex flex-wrap justify-content-end align-items-start gap-3"
          )}
        >
          {showReceipt && (
            <PortalButton
              appearance="text"
              disabled={!order || receiptDownloadingIds.has(charge.id)}
              onClick={() => handleDownloadReceipt(charge)}
            >
              Receipt
            </PortalButton>
          )}
          {!isMobile && (
            <PortalButton
              appearance="text"
              onClick={() => handleClickChargeDetails(charge)}
            >
              Details
            </PortalButton>
          )}
        </div>
      </div>
    );
  };

  const numberOfDefaultDisplayedCharges = 2;
  const hasMoreThanDefaultCharges = !!(
    chargesData && chargesData?.length > numberOfDefaultDisplayedCharges
  );
  const defaultDisplayedCharges = chargesData?.slice(
    0,
    numberOfDefaultDisplayedCharges
  );
  const displayedCharges = viewAllCharges
    ? chargesData
    : defaultDisplayedCharges;

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center">
        <div className="fw-bold fs-3">Charges</div>
        {hasMoreThanDefaultCharges && (
          <PortalButton
            appearance="text"
            onClick={() => setViewAllCharges((v) => !v)}
          >
            {viewAllCharges ? "See less" : "View all"}
          </PortalButton>
        )}
      </div>
      <Spacer size={24} />
      <div>
        <div className="row py-3 border-bottom">
          <b className={columnClasses.date}>Date</b>
          <b className={columnClasses.membershipPlan}>Membership plan</b>
          <b className={columnClasses.paymentMethod}>Payment method</b>
          <b className={columnClasses.total}>Total</b>
        </div>
        {displayedCharges?.map(mapChargeRow)}
      </div>

      {viewingChargeDetails && (
        <PortalBillBreakdownModal
          show={!!viewingChargeDetails}
          onHide={() => setViewingChargeDetails(undefined)}
          totalAmount={viewingChargeDetails.totalPrice}
          currency={viewingChargeDetails.currency}
          monthlyPaymentAmount={viewingChargeDetails.unitPrice}
          taxAmount={viewingChargeDetails.taxAmount}
          taxDescription={viewingChargeDetails.taxDescription}
          promotionAmount={viewingChargeDetails.promotionAmount}
          promotionCode={viewingChargeDetails.promotionCode}
          isRefunded={viewingChargeDetails.transactionType === "refund"}
          paymentCycles={
            viewingChargeDetails.isFirstCharge &&
            viewingChargeDetails.adjustmentAmount !== 0
              ? viewingChargeDetails.paymentCycles
              : viewingChargeDetails.paymentCycles - 1
          }
          adjustmentAmount={viewingChargeDetails.adjustmentAmount}
          refundedAmount={viewingChargeDetails.refundedAmount}
          feeLines={viewingChargeDetails.feeLines}
          confirmationId={order?.confirmationID}
        />
      )}
    </div>
  );
}
